import { useVirtualizer } from "@tanstack/react-virtual";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

export type VirtualizedGridProps<T> = {
  /** Items to virtualize */
  items: T[];
  /** Number of columns in the grid */
  columns: number;
  /**
   * The scroll container element. Pass `null` while the element is still
   * mounting; the grid will render nothing until it is available. Use a
   * callback ref + state in the parent to capture the element, since
   * `useRef` does not trigger re-renders when `.current` changes — which
   * matters when the container is rendered inside a Base UI Tabs.Panel
   * that defers mounting.
   */
  scrollElement: HTMLElement | null;
  /** Render function for each item */
  renderItem: (item: T, index: number) => ReactNode;
  /** Estimated height of each row (in px) */
  estimateRowSize: number;
  /** Get unique key for each item */
  getItemKey: (item: T) => string;
  /** Extra rows above/below viewport for smoother scrolling */
  overscan?: number;
  /** Gap between rows (in px) */
  rowGapPx?: number;
  /** Tailwind classes for the grid row container */
  gridClassName?: string;
};

const DEFAULT_OVERSCAN = 5;
const DEFAULT_ROW_GAP_PX = 0;

function chunkArray<T>(items: T[], size: number): T[][] {
  if (size <= 0) {
    throw new Error(`chunkArray: size must be positive, received ${size}`);
  }
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

type VirtualizedGridContentProps<T> = Omit<
  VirtualizedGridProps<T>,
  "scrollElement"
> & {
  scrollElement: HTMLElement;
};

function VirtualizedGridContent<T>({
  items,
  columns,
  scrollElement,
  renderItem,
  estimateRowSize,
  getItemKey,
  overscan = DEFAULT_OVERSCAN,
  rowGapPx = DEFAULT_ROW_GAP_PX,
  gridClassName = "grid grid-cols-1 gap-4",
}: VirtualizedGridContentProps<T>) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) {
      return;
    }

    const updateScrollMargin = () => {
      const containerRect = scrollElement.getBoundingClientRect();
      const gridRect = grid.getBoundingClientRect();
      const offset = gridRect.top - containerRect.top + scrollElement.scrollTop;
      setScrollMargin(Math.max(0, Math.round(offset)));
    };

    updateScrollMargin();

    const observer = new ResizeObserver(updateScrollMargin);
    observer.observe(grid);
    observer.observe(scrollElement);

    return () => observer.disconnect();
  }, [scrollElement]);

  const rows = useMemo(() => {
    const chunks = chunkArray(items, columns);
    return chunks.map((chunk, rowIndex) =>
      chunk.map((item, colIndex) => ({
        item,
        index: rowIndex * columns + colIndex,
      }))
    );
  }, [items, columns]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => estimateRowSize + rowGapPx,
    overscan,
    scrollMargin,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    <div
      className="relative w-full"
      data-testid="virtualized-grid"
      ref={gridRef}
      style={{ height: totalSize }}
    >
      {virtualItems.map((virtualRow) => {
        const row = rows[virtualRow.index];
        if (!row) {
          return null;
        }

        return (
          <div
            className="absolute top-0 left-0 w-full"
            data-index={virtualRow.index}
            key={virtualRow.key}
            ref={virtualizer.measureElement}
            style={{
              transform: `translateY(${virtualRow.start - scrollMargin}px)`,
              paddingBottom: rowGapPx,
            }}
          >
            <div className={gridClassName}>
              {row.map(({ item, index }) => (
                <div key={getItemKey(item)}>{renderItem(item, index)}</div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function VirtualizedGrid<T>(props: VirtualizedGridProps<T>) {
  const { items, columns, scrollElement } = props;

  if (columns <= 0) {
    throw new Error(
      `VirtualizedGrid: columns must be positive, received ${columns}`
    );
  }

  if (items.length === 0 || !scrollElement) {
    return null;
  }

  return <VirtualizedGridContent {...props} scrollElement={scrollElement} />;
}
